import '../../styles.scss';
import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { Dropdown } from 'semantic-ui-react';
import { DealSummaryActionsDropdownOptions, DealSummaryActionStatus } from '~/constants/deal-summary';
import { usePageType, usePortfolioContext } from '~/hooks';
import useArchiveToast from '~/hooks/archive/useArchiveToast';
import { getInvestorProjectDataroomAccessKey, useInvestorDataroomAccess } from '~/requests/dataroom';
import Messages from '../Messages';
import { useGrantInvestorDataroomAccess } from '~/mutations/dataroom/investorAccess/useGrantInvestorDataroomAccess';
import { useRevokeInvestorDataroomAccess } from '~/mutations/dataroom/investorAccess/useRevokeInvestorDataroomAccess';
import { DataroomAccess } from '~/types/dataroom';

interface ActionDropdownMenuProps {
  investor: any;
  awaitingBid: boolean;
  setDeclineInvestorId?: (id: string) => void;
}

type DropdownOption = {
  text: string;
  value: string;
}

const ActionsDropdownMenu = ({
  investor,
  awaitingBid,
  setDeclineInvestorId,
}: ActionDropdownMenuProps) => {
  const { portfolio } = usePortfolioContext();
  const queryClient = useQueryClient();
  const pageType = usePageType();
  const { showArchiveToast } = useArchiveToast();

  const { data: investorDataroomAccess, status: getInvestorProjectDataroomAccessStatus, refetch: refetchInvestorDataroomAccess } = useInvestorDataroomAccess();
  const { mutate: grantInvestorDataroomAccess, status: grantInvestorDataroomAccessStatus } = useGrantInvestorDataroomAccess();
  const { mutate: revokeInvestorDataroomAccess, status: revokeInvestorDataroomAccessStatus } = useRevokeInvestorDataroomAccess();
  const loadingDataroomAccess = getInvestorProjectDataroomAccessStatus === 'loading' || grantInvestorDataroomAccessStatus === 'loading' || revokeInvestorDataroomAccessStatus === 'loading';

  const hasDataroomAccess = (investorId: string) => {
    return !!investorDataroomAccess?.investors?.find((row: DataroomAccess) => row.investorId === investorId);
  };

  const toggleDataroomAccess = async (investorId: string) => {
    if (showArchiveToast()) {
      return;
    }

    if (hasDataroomAccess(investorId)) {
      // @ts-ignore
      await revokeInvestorDataroomAccess({ investorId }, { onSuccess: refetchInvestorDataroomAccess });
    } else {
      // @ts-ignore
      await grantInvestorDataroomAccess({ investorId }, { onSuccess: refetchInvestorDataroomAccess });
    }
  };

  const handleClick = (option: DropdownOption) => {
    if (option.value === DealSummaryActionStatus.GRANT_DOCUMENT_ACCESS) {
      toggleDataroomAccess(investor?.id);
    } else if (option.value === DealSummaryActionStatus.END_CONVERSATION) {
      setDeclineInvestorId?.(investor?.id);
    }
  };

  // This is required to keep dataroom access in sync with portfolio's bid summary
  useEffect(() => {
    if (pageType === 'portfolio') {
      queryClient.invalidateQueries(getInvestorProjectDataroomAccessKey(pageType, portfolio?.id));
    }
  }, [pageType, portfolio?.id, queryClient]);

  return (
    <>
      {DealSummaryActionsDropdownOptions?.map((option: DropdownOption) => {
        let dropdownContent;
        if (option.value === DealSummaryActionStatus.VIEW_MESSAGES) {
          if (!awaitingBid) {
            dropdownContent = (
              <Messages
                portfolio={portfolio}
                investor={investor}
                awaitingBid={awaitingBid}
              />
            );
          } else {
            return null;
          }
        } else if (option.value === DealSummaryActionStatus.GRANT_DOCUMENT_ACCESS) {
          dropdownContent = hasDataroomAccess(investor?.id)
            ? 'Revoke document access'
            : option.text;
        } else {
          dropdownContent = option.text;
        }

        return (
          <Dropdown.Item
            size="small"
            key={option.value}
            className='portfolio-actions'
            style={{ ['--actions-color' as string]: option.value === DealSummaryActionStatus.END_CONVERSATION ? 'var(--color-red)' : 'var(--color-charcoal)' }}
            disabled={loadingDataroomAccess}
            onClick={() => handleClick(option)}
          >
            {dropdownContent}
          </Dropdown.Item>
        );
      })}
    </>
  );
};

export default ActionsDropdownMenu;